import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  ArrowLeft,
  Mail,
  Phone,
  Calendar,
  Users,
  Clock,
  AlertCircle,
  Edit,
  UserX,
  UserCheck,
  DollarSign,
  Percent,
  TrendingUp,
  CreditCard,
  MapPin,
  Building2,
  Shield,
  UserCog,
  Briefcase,
  Activity,
  BarChart3,
  ArrowRight,
  CheckCircle,
  RefreshCw,
  FileText,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';
import EditRepresentativeDialog from '../../components/admin/EditRepresentativeDialog';

const REP_TYPE_CONFIG = {
  rep1: {
    label: 'Internal Representative',
    shortLabel: 'Internal',
    color: 'bg-blue-100 text-blue-700',
    gradient: 'from-blue-500 to-indigo-600',
    icon: Shield,
  },
  rep2: {
    label: 'Senior/Regional Representative',
    shortLabel: 'Senior/Regional',
    color: 'bg-purple-100 text-purple-700',
    gradient: 'from-purple-500 to-violet-600',
    icon: UserCog,
  },
  rep3: {
    label: 'Partner Representative',
    shortLabel: 'Partner',
    color: 'bg-amber-100 text-amber-700',
    gradient: 'from-amber-500 to-orange-600',
    icon: Building2,
  },
};

const EXPERIENCE_LABELS = {
  junior: 'Junior',
  mid: 'Mid-Level',
  senior: 'Senior',
  lead: 'Lead',
  director: 'Director',
};

export default function RepresentativeDetail() {
  const { repId } = useParams();
  const navigate = useNavigate();
  const [rep, setRep] = useState(null);
  const [assignedStudents, setAssignedStudents] = useState([]);
  const [referredStudents, setReferredStudents] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [stats, setStats] = useState({
    totalStudents: 0,
    referredStudents: 0,
    totalEarnings: 0,
    pendingEarnings: 0,
    paidCommissions: 0,
    pendingCommissions: 0,
    applicationsProcessed: 0,
    conversionRate: 0,
  });
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('students');

  useEffect(() => {
    fetchRepDetails();
  }, [repId]);

  const fetchRepDetails = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getRepresentativeById(repId);
      setRep(response.representative);
      setAssignedStudents(response.assignedStudents || []);
      setReferredStudents(response.referredStudents || []);
      setCommissions(response.commissions || []);
      setActivityLog(response.activityLog || []);
      setStats(
        response.stats || {
          totalStudents: 0,
          referredStudents: 0,
          totalEarnings: 0,
          pendingEarnings: 0,
          paidCommissions: 0,
          pendingCommissions: 0,
          applicationsProcessed: 0,
          conversionRate: 0,
        }
      );
    } catch (error) {
      toast.error('Failed to fetch representative details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusToggle = async () => {
    try {
      const newStatus = !rep.isActive;
      await adminAPI.updateRepresentativeStatus(repId, newStatus);
      toast.success(`Representative ${newStatus ? 'activated' : 'deactivated'}`);
      fetchRepDetails();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      approved: { bg: 'bg-blue-100', text: 'text-blue-700' },
      paid: { bg: 'bg-green-100', text: 'text-green-700' },
      rejected: { bg: 'bg-red-100', text: 'text-red-700' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700' },
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
      IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-700' },
      ASSIGNED: { bg: 'bg-purple-100', text: 'text-purple-700' },
      COMPLETED: { bg: 'bg-green-100', text: 'text-green-700' },
      active: { bg: 'bg-green-100', text: 'text-green-700' },
      inactive: { bg: 'bg-stone-100', text: 'text-gray-700' },
    };
    const config = statusConfig[status] || { bg: 'bg-stone-100', text: 'text-gray-700' };
    return (
      <Badge className={`${config.bg} ${config.text} text-xs`}>
        {status?.replace(/_/g, ' ') || 'Unknown'}
      </Badge>
    );
  };

  const getActionLabel = (action) => {
    const labels = {
      user_created: 'Account created',
      user_updated: 'Profile updated',
      user_activated: 'Account activated',
      user_deactivated: 'Account deactivated',
      commission_created: 'Commission earned',
      commission_approved: 'Commission approved',
      commission_paid: 'Commission paid',
      service_request_created: 'Service request created',
      service_request_status_changed: 'Service request updated',
      task_created: 'Task created',
      task_status_changed: 'Task updated',
      agent_assigned: 'Assigned to student',
      password_reset: 'Password reset',
    };
    return labels[action] || action?.replace(/_/g, ' ') || 'Unknown action';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-[#F2575F] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!rep) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-96">
          <AlertCircle className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Representative Not Found</h2>
          <p className="text-gray-500 mb-4">
            The representative you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate('/admin/representatives')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Representatives
          </Button>
        </div>
      </AdminLayout>
    );
  }

  const config = REP_TYPE_CONFIG[rep.role] || REP_TYPE_CONFIG.rep1;
  const TypeIcon = config.icon;
  const profile = rep.profile || {};

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/admin/representatives')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-outfit font-bold text-gray-900">
              Representative Details
            </h1>
            <p className="text-gray-600">
              View and manage representative information, students, and commissions
            </p>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="p-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Avatar and Basic Info */}
            <div className="flex items-start gap-4">
              <div
                className={`w-20 h-20 bg-gradient-to-br ${config.gradient} rounded-full flex items-center justify-center text-white font-bold text-2xl`}
              >
                {rep.firstName?.[0]}
                {rep.lastName?.[0]}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {rep.firstName} {rep.lastName}
                </h2>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge className={config.color}>
                    <TypeIcon className="w-3 h-3 mr-1" />
                    {config.shortLabel}
                  </Badge>
                  <Badge
                    className={
                      rep.isActive !== false
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }
                  >
                    {rep.isActive !== false ? 'Active' : 'Inactive'}
                  </Badge>
                  {profile.experienceLevel && (
                    <Badge className="bg-stone-100 text-gray-700">
                      {EXPERIENCE_LABELS[profile.experienceLevel] || profile.experienceLevel}
                    </Badge>
                  )}
                </div>
                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span>{rep.email}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-4 h-4" />
                    <span>{rep.phone || 'Not provided'}</span>
                  </div>
                  {(profile.assignedRegion || rep.country) && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{profile.assignedRegion || rep.country}</span>
                    </div>
                  )}
                  {profile.organization && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Building2 className="w-4 h-4" />
                      <span>{profile.organization}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Joined {formatDate(rep.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Commission & Performance Info */}
            <div className="lg:ml-8 flex flex-col sm:flex-row lg:flex-col gap-4">
              <div className="p-4 bg-stone-50 rounded-lg flex-1">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Commission Settings</h3>
                <div className="flex items-center gap-2">
                  <Percent className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold text-gray-900">
                      {rep.commissionRate || rep.commissionPercentage || 10}%
                    </p>
                    <p className="text-xs text-gray-500">Commission Rate</p>
                  </div>
                </div>
              </div>
              {stats.conversionRate > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg flex-1">
                  <h3 className="text-sm font-medium text-blue-700 mb-3">Conversion Rate</h3>
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="text-2xl font-bold text-blue-700">
                        {stats.conversionRate}%
                      </p>
                      <p className="text-xs text-blue-500">Student Conversion</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="lg:ml-auto flex flex-wrap gap-2 items-start">
              <Button variant="outline" onClick={() => setEditDialogOpen(true)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
              <Button
                variant={rep.isActive !== false ? 'destructive' : 'default'}
                onClick={handleStatusToggle}
              >
                {rep.isActive !== false ? (
                  <>
                    <UserX className="w-4 h-4 mr-2" />
                    Deactivate
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 mr-2" />
                    Activate
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Bio */}
          {profile.bio && (
            <div className="mt-4 pt-4 border-t border-stone-100">
              <p className="text-sm text-gray-600">{profile.bio}</p>
            </div>
          )}
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              label: 'Total Students',
              value: stats.totalStudents,
              icon: Users,
              color: 'blue',
              delay: 0.1,
            },
            {
              label: 'Total Earnings',
              value: formatCurrency(stats.totalEarnings),
              icon: DollarSign,
              color: 'green',
              delay: 0.2,
              isGreen: true,
            },
            {
              label: 'Pending Earnings',
              value: formatCurrency(stats.pendingEarnings),
              icon: Clock,
              color: 'amber',
              delay: 0.3,
              isAmber: true,
            },
            {
              label: 'Referred Students',
              value: stats.referredStudents,
              icon: TrendingUp,
              color: 'purple',
              delay: 0.4,
            },
          ].map((stat) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: stat.delay }}
              >
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg bg-${stat.color}-100 flex items-center justify-center`}
                    >
                      <Icon className={`w-5 h-5 text-${stat.color}-600`} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{stat.label}</p>
                      <p
                        className={`text-2xl font-bold ${
                          stat.isGreen
                            ? 'text-green-600'
                            : stat.isAmber
                              ? 'text-amber-600'
                              : 'text-gray-900'
                        }`}
                      >
                        {stat.value}
                      </p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Tabs */}
        <Card className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="students" className="gap-2">
                <Users className="w-4 h-4" />
                Students ({assignedStudents.length})
              </TabsTrigger>
              <TabsTrigger value="referred" className="gap-2">
                <TrendingUp className="w-4 h-4" />
                Referred ({referredStudents.length})
              </TabsTrigger>
              <TabsTrigger value="commissions" className="gap-2">
                <CreditCard className="w-4 h-4" />
                Commissions ({commissions.length})
              </TabsTrigger>
              <TabsTrigger value="lifecycle" className="gap-2">
                <FileText className="w-4 h-4" />
                Student Lifecycle
              </TabsTrigger>
              <TabsTrigger value="activity" className="gap-2">
                <Activity className="w-4 h-4" />
                Activity Log
              </TabsTrigger>
            </TabsList>

            {/* Assigned Students Tab */}
            <TabsContent value="students">
              {assignedStudents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Users className="w-12 h-12 mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No students assigned</p>
                  <p className="text-sm">
                    Students will appear here once assigned to this representative
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Services</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignedStudents.map((student, index) => (
                        <TableRow
                          key={student._id || student.studentId || index}
                          className="cursor-pointer hover:bg-stone-50"
                          onClick={() => navigate(`/admin/students/${student.studentId}`)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {student.user?.firstName?.[0] || '?'}
                                {student.user?.lastName?.[0] || ''}
                              </div>
                              <span className="font-medium">
                                {student.user?.firstName || 'Unknown'}{' '}
                                {student.user?.lastName || ''}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-600">
                            {student.user?.email || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {student.destinationCountry || student.country || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {student.services?.length || 0} service(s)
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {student.status
                              ? getStatusBadge(student.status)
                              : student.services?.[0]?.status &&
                                getStatusBadge(student.services[0].status)}
                          </TableCell>
                          <TableCell className="text-gray-600">
                            {formatDate(
                              student.services?.[0]?.assignedAt || student.createdAt
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Referred Students Tab */}
            <TabsContent value="referred">
              {referredStudents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <TrendingUp className="w-12 h-12 mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No referred students</p>
                  <p className="text-sm">
                    Students referred by this representative will appear here
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Joined Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {referredStudents.map((student, index) => (
                        <TableRow
                          key={student._id || student.studentId || index}
                          className="cursor-pointer hover:bg-stone-50"
                          onClick={() => navigate(`/admin/students/${student.studentId}`)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-gradient-to-br from-[#F2575F] to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                {student.user?.firstName?.[0] || '?'}
                                {student.user?.lastName?.[0] || ''}
                              </div>
                              <span className="font-medium">
                                {student.user?.firstName || 'Unknown'}{' '}
                                {student.user?.lastName || ''}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-600">
                            {student.user?.email || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {student.destinationCountry || student.country || 'N/A'}
                          </TableCell>
                          <TableCell>
                            {student.status ? getStatusBadge(student.status) : 'N/A'}
                          </TableCell>
                          <TableCell className="text-gray-600">
                            {formatDate(student.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Commissions Tab */}
            <TabsContent value="commissions">
              {commissions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <CreditCard className="w-12 h-12 mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No commission history</p>
                  <p className="text-sm">Commission records will appear here</p>
                </div>
              ) : (
                <>
                  {/* Commission Summary Row */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                      <p className="text-lg font-bold text-green-700">
                        {formatCurrency(stats.totalEarnings)}
                      </p>
                      <p className="text-xs text-green-600">
                        Paid ({stats.paidCommissions})
                      </p>
                    </div>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
                      <p className="text-lg font-bold text-amber-700">
                        {formatCurrency(stats.pendingEarnings)}
                      </p>
                      <p className="text-xs text-amber-600">
                        Pending ({stats.pendingCommissions})
                      </p>
                    </div>
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
                      <p className="text-lg font-bold text-blue-700">
                        {formatCurrency(
                          stats.totalEarnings + stats.pendingEarnings
                        )}
                      </p>
                      <p className="text-xs text-blue-600">Total Lifetime</p>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Reference</TableHead>
                          <TableHead>Student</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {commissions.map((commission, index) => (
                          <TableRow
                            key={commission.commissionId || commission._id || index}
                          >
                            <TableCell className="font-mono text-sm">
                              {commission.referenceId ||
                                (commission.commissionId || commission._id || '').slice(
                                  0,
                                  8
                                ) + '...'}
                            </TableCell>
                            <TableCell>
                              {commission.studentName || 'N/A'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {commission.commissionType === 'APPLICATION'
                                  ? 'Application'
                                  : commission.serviceType?.replace(/_/g, ' ') ||
                                    'Service'}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-semibold text-green-600">
                              {formatCurrency(commission.amount)}
                            </TableCell>
                            <TableCell>
                              {getStatusBadge(commission.status)}
                            </TableCell>
                            <TableCell className="text-gray-600">
                              {formatDate(commission.createdAt)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Student Lifecycle Tab */}
            <TabsContent value="lifecycle">
              {(() => {
                // Merge assigned + referred into a unified lifecycle view
                const allStudents = [
                  ...assignedStudents.map(s => ({ ...s, relationship: 'assigned' })),
                  ...referredStudents
                    .filter(rs => !assignedStudents.some(as => (as.studentId || as._id) === (rs.studentId || rs._id)))
                    .map(s => ({ ...s, relationship: 'referred' })),
                ];

                if (allStudents.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                      <FileText className="w-12 h-12 mb-4 text-gray-300" />
                      <p className="text-lg font-medium">No student lifecycle data</p>
                      <p className="text-sm">
                        Students and their service journey will appear here
                      </p>
                    </div>
                  );
                }

                // Lifecycle stage calculation
                const getLifecycleStage = (student) => {
                  const services = student.services || [];
                  if (services.length === 0) return { stage: 'Registered', color: 'bg-gray-100 text-gray-700', icon: Users, progress: 10 };
                  const hasCompleted = services.some(s => s.status === 'COMPLETED');
                  const hasInProgress = services.some(s => ['IN_PROGRESS', 'ASSIGNED'].includes(s.status));
                  const hasPending = services.some(s => s.status === 'PENDING_ADMIN_ASSIGNMENT');
                  if (hasCompleted && !hasInProgress) return { stage: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle, progress: 100 };
                  if (hasCompleted && hasInProgress) return { stage: 'Partially Complete', color: 'bg-blue-100 text-blue-700', icon: RefreshCw, progress: 75 };
                  if (hasInProgress) return { stage: 'In Progress', color: 'bg-purple-100 text-purple-700', icon: RefreshCw, progress: 50 };
                  if (hasPending) return { stage: 'Applied', color: 'bg-amber-100 text-amber-700', icon: Clock, progress: 25 };
                  return { stage: 'Active', color: 'bg-blue-100 text-blue-700', icon: Users, progress: 20 };
                };

                // Stage summary counts
                const stageCounts = allStudents.reduce((acc, s) => {
                  const { stage } = getLifecycleStage(s);
                  acc[stage] = (acc[stage] || 0) + 1;
                  return acc;
                }, {});

                return (
                  <div className="space-y-4">
                    {/* Stage Summary Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { stage: 'Registered', color: 'bg-gray-50 border-gray-200', textColor: 'text-gray-700' },
                        { stage: 'Applied', color: 'bg-amber-50 border-amber-200', textColor: 'text-amber-700' },
                        { stage: 'In Progress', color: 'bg-purple-50 border-purple-200', textColor: 'text-purple-700' },
                        { stage: 'Completed', color: 'bg-green-50 border-green-200', textColor: 'text-green-700' },
                      ].map(({ stage, color, textColor }) => (
                        <div key={stage} className={`p-3 rounded-lg border ${color} text-center`}>
                          <p className={`text-xl font-bold ${textColor}`}>{stageCounts[stage] || 0}</p>
                          <p className="text-xs text-gray-600">{stage}</p>
                        </div>
                      ))}
                    </div>

                    {/* Student Lifecycle Cards */}
                    <div className="space-y-3">
                      {allStudents.map((student, index) => {
                        const lifecycle = getLifecycleStage(student);
                        const LifecycleIcon = lifecycle.icon;
                        const services = student.services || [];
                        const studentName = `${student.user?.firstName || 'Unknown'} ${student.user?.lastName || ''}`.trim();

                        return (
                          <motion.div
                            key={student._id || student.studentId || index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                          >
                            <Card
                              className="p-4 border border-stone-200 hover:border-stone-300 cursor-pointer transition-colors"
                              onClick={() => navigate(`/admin/students/${student.studentId}`)}
                            >
                              <div className="flex items-center gap-4">
                                {/* Avatar */}
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0">
                                  {student.user?.firstName?.[0] || '?'}{student.user?.lastName?.[0] || ''}
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="font-semibold text-gray-900 text-sm truncate">{studentName}</p>
                                    <Badge className={`text-xs ${lifecycle.color}`}>
                                      <LifecycleIcon className="w-3 h-3 mr-1" />
                                      {lifecycle.stage}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs">
                                      {student.relationship === 'referred' ? 'Referred' : 'Assigned'}
                                    </Badge>
                                  </div>

                                  {/* Progress Bar */}
                                  <div className="flex items-center gap-2 mb-1.5">
                                    <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                      <div
                                        className="bg-green-500 h-1.5 rounded-full transition-all"
                                        style={{ width: `${lifecycle.progress}%` }}
                                      />
                                    </div>
                                    <span className="text-xs text-gray-500">{lifecycle.progress}%</span>
                                  </div>

                                  {/* Service Badges */}
                                  {services.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {services.slice(0, 4).map((svc, sIdx) => (
                                        <Badge
                                          key={sIdx}
                                          variant="outline"
                                          className={`text-xs ${
                                            svc.status === 'COMPLETED'
                                              ? 'bg-green-50 text-green-700 border-green-200'
                                              : svc.status === 'IN_PROGRESS'
                                                ? 'bg-purple-50 text-purple-700 border-purple-200'
                                                : 'bg-gray-50 text-gray-600 border-gray-200'
                                          }`}
                                        >
                                          {svc.serviceType?.replace(/_/g, ' ').slice(0, 20) || 'Service'}
                                        </Badge>
                                      ))}
                                      {services.length > 4 && (
                                        <Badge variant="outline" className="text-xs">+{services.length - 4} more</Badge>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Arrow */}
                                <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              </div>
                            </Card>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </TabsContent>

            {/* Activity Log Tab */}
            <TabsContent value="activity">
              {activityLog.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Activity className="w-12 h-12 mb-4 text-gray-300" />
                  <p className="text-lg font-medium">No activity recorded</p>
                  <p className="text-sm">
                    Actions and events related to this representative will appear here
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activityLog.map((log, index) => (
                    <motion.div
                      key={log.logId || log._id || index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className="flex items-start gap-4 p-3 bg-stone-50 rounded-lg border border-stone-100"
                    >
                      <div className="w-8 h-8 bg-white border border-stone-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Activity className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {getActionLabel(log.action)}
                        </p>
                        {log.details && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate">
                            {typeof log.details === 'string'
                              ? log.details
                              : JSON.stringify(log.details).slice(0, 120)}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {log.actorUserId === repId ? 'Self' : 'By Admin'} &middot;{' '}
                          {formatDateTime(log.timestamp || log.createdAt)}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      {/* Edit Dialog */}
      <EditRepresentativeDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        representative={rep}
        onSuccess={fetchRepDetails}
      />
    </AdminLayout>
  );
}
